// src/pages/PhoneCallsClinic/index.js
//
// Telefon Görüşmeleri — klinik geneli görüşme kayıtları. Düz liste
// (docs/design-principles.md §3): arama + tarih → geri arama çipleri → çerçevesiz
// tablo. Satıra tıklayınca sağdan drawer'da PhoneCallEditor; altta yetkiye bağlı
// "Hastaya git". Düzenleme "phoneCalls:update".

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";
import { Phone as PhoneIcon } from "@mui/icons-material";
import PhoneCallEditor from "../../components/organisms/PatientWorkspaceTabs/PhoneCallEditor";
import useAuth from "../../hooks/useAuth";
import useTerms from "../../hooks/useTerms";
import { useHasPermission } from "../../hooks/useHasPermission";
import { useAlert } from "../../context/AlertProvider";
import { useClinicTheme } from "../../theme";
import { useListClinicPhoneCallsMutation } from "../../store/secretaryApi";
import { DataTableCard } from "../../components/organisms";
import { FormDatePicker } from "../../components/atoms";
import {
  ClinicRecordListLayout,
  RecordEditorDrawer,
  ClickableRow,
  DateCell,
  EDGE_L,
  EDGE_R,
  FillerRow,
  PatientCell,
  PlainHead,
  ROWS_PER_PAGE_OPTIONS,
  SORTERS,
  SkeletonRows,
  SortHead,
  StatusPill,
  TextCell,
  cellSx,
  patientNameOf,
  useClientList,
} from "../../components/organisms/ClinicRecordList";
import { fullDateTime } from "../Secretary/PatientsPage/patientListFormat";

const COLS = 5;

const SEGMENTS = [
  { value: "all", label: "Tümü" },
  { value: "callback", label: "Geri arama gerekli" },
  { value: "none", label: "Geri arama yok" },
];

const SEGMENT_EMPTY = {
  callback: "Geri arama gerektiren görüşme yok.",
  none: "Geri arama gerektirmeyen görüşme yok.",
};

const staffName = (u) => `${u?.name || ""} ${u?.surname || ""}`.trim();
const callerOf = (r) => String(r.caller || "").trim() || staffName(r.createdBy);
const segmentOf = (r) => (r.callback ? "callback" : "none");

/** "YYYY-MM-DD" — saat farkı gün eşleşmesini bozmasın. */
const toYMD = (value) => {
  const d = value ? new Date(value) : null;
  if (!d || Number.isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const matches = (r, text) =>
  patientNameOf(r.patient).toLocaleLowerCase("tr").includes(text) ||
  String(r.patient?.tcNo || "").includes(text) ||
  String(r.subject || "").toLocaleLowerCase("tr").includes(text) ||
  callerOf(r).toLocaleLowerCase("tr").includes(text);

const SORT_FNS = { name: SORTERS.name((r) => r.patient), date: SORTERS.date((r) => r.date) };
const DEFAULT_DIR = { name: "asc", date: "desc" };

export default function PhoneCallsClinic() {
  const terms = useTerms();
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const { palette } = useClinicTheme();
  const { showError } = useAlert();
  const canEdit = useHasPermission("phoneCalls:update");

  const [listClinicPhoneCalls, { isLoading }] = useListClinicPhoneCallsMutation();
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [dateFilter, setDateFilter] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const selected = useMemo(() => rows.find((r) => r._id === selectedId) || null, [rows, selectedId]);

  const refresh = useCallback(async () => {
    if (!token) return;
    try {
      const res = await listClinicPhoneCalls({ token }).unwrap();
      setRows(res?.data?.list || []);
    } catch (_) {
      showError("Telefon görüşmeleri yüklenemedi.");
    } finally {
      setLoaded(true);
    }
  }, [token, listClinicPhoneCalls, showError]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Tarih filtresi listeyi önceden daraltır; çip sayıları buna göre
  const dated = useMemo(
    () => (dateFilter ? rows.filter((r) => toYMD(r.date) === dateFilter) : rows),
    [rows, dateFilter]
  );

  const list = useClientList(dated, {
    segmentOf,
    matches,
    sorters: SORT_FNS,
    defaultDir: DEFAULT_DIR,
    initialSort: "date",
  });

  const segments = useMemo(
    () => SEGMENTS.map((s) => ({ value: s.value, label: `${s.label} · ${list.counts[s.value] || 0}` })),
    [list.counts]
  );

  const handleSaved = useCallback((updated) => {
    if (!updated?._id) return;
    setRows((prev) => prev.map((r) => (r._id === updated._id ? { ...r, ...updated, patient: r.patient } : r)));
  }, []);

  const handleEditorError = useCallback(
    (err) => {
      const status = err?.status || err?.originalStatus;
      const serverMsg = err?.data?.message;
      if (status === 423) {
        showError(serverMsg || `Bu ${terms.patientLower} şu an ${terms.examLoc} — değişiklik kaydedilemedi.`);
        return;
      }
      showError(serverMsg || "Görüşme kaydedilemedi.");
    },
    [showError, terms]
  );

  const busy = isLoading && !loaded;
  const empty = list.searchText
    ? { title: "Görüşme bulunamadı", message: `“${list.searchText}” aramanızla eşleşen görüşme yok. Aramayı değiştirin.` }
    : dateFilter
    ? { title: "Görüşme bulunamadı", message: "Seçilen günde kayıtlı görüşme yok." }
    : list.segment !== "all"
    ? { title: "Bu filtrede görüşme yok", message: SEGMENT_EMPTY[list.segment] }
    : { title: "Henüz görüşme yok", message: `Görüşmeler ${terms.patientLower} detayındaki Telefon Görüşmeleri sekmesinden kaydedilir.` };

  const sortProps = { sort: list.sort, dir: list.dir, onSort: list.onSort };

  return (
    <>
      <ClinicRecordListLayout
        icon={<PhoneIcon sx={{ fontSize: 22 }} />}
        title="Telefon Görüşmeleri"
        subtitle={`${terms.patients}la yapılan görüşmeler ve geri aramalar`}
        search={list.search}
        onSearchChange={list.setSearch}
        searchPlaceholder={`${terms.patient} adı, TC no, konu veya görüşen ile ara`}
        filters={
          <FormDatePicker
            placeholder="Görüşme tarihi"
            value={dateFilter}
            onChange={(v) => {
              setDateFilter(v);
              list.setSegment(list.segment);
            }}
            sx={{ width: 180 }}
          />
        }
        segments={segments}
        segment={list.segment}
        onSegmentChange={list.setSegment}
        segmentAriaLabel="Geri arama durumu"
      >
        <DataTableCard
      loading={Boolean(busy)}
          variant="flat"
          empty={!busy && list.filtered.length === 0}
          emptyIcon={<PhoneIcon />}
          emptyTitle={empty.title}
          emptyMessage={empty.message}
          page={list.page}
          rowsPerPage={list.rowsPerPage}
          totalCount={list.filtered.length}
          onPageChange={list.onPageChange}
          onRowsPerPageChange={list.onRowsPerPageChange}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        >
          <Table stickyHeader size="small" sx={{ tableLayout: "fixed", minWidth: 960 }}>
            <TableHead>
              <TableRow>
                <SortHead field="name" label={terms.patient} {...sortProps} sx={{ ...EDGE_L, width: "26%" }} />
                <PlainHead sx={{ width: "32%" }}>Konu</PlainHead>
                <PlainHead sx={{ width: "16%" }}>Görüşen</PlainHead>
                <SortHead field="date" label="Tarih" defaultDir="desc" {...sortProps} sx={{ width: "12%" }} />
                <PlainHead sx={{ ...EDGE_R, width: "14%" }}>Geri arama</PlainHead>
              </TableRow>
            </TableHead>
            <TableBody>
              {busy ? (
                <SkeletonRows rows={Math.min(10, list.rowsPerPage)} cols={COLS} />
              ) : list.pagedRows.length ? (
                list.pagedRows.map((row) => {
                  const subject = String(row.subject || "").trim();
                  const desc = String(row.description || "").trim();
                  return (
                    <ClickableRow key={row._id} onClick={() => setSelectedId(row._id)} palette={palette}>
                      <PatientCell patient={row.patient} />
                      <TextCell main={subject || null} sub={desc || null} tooltip={desc.length > 60 ? desc : null} />
                      <TextCell main={callerOf(row) || null} />
                      <DateCell value={row.date} />
                      <TableCell sx={{ ...cellSx, ...EDGE_R }}>
                        <StatusPill
                          label={row.callback ? "Gerekli" : "Yok"}
                          tone={row.callback ? "warning" : "neutral"}
                          palette={palette}
                        />
                      </TableCell>
                    </ClickableRow>
                  );
                })
              ) : (
                <FillerRow cols={COLS} />
              )}
            </TableBody>
          </Table>
        </DataTableCard>
      </ClinicRecordListLayout>

      <RecordEditorDrawer
        open={Boolean(selected)}
        onClose={() => setSelectedId(null)}
        icon={<PhoneIcon />}
        title={selected ? patientNameOf(selected.patient) : "Görüşme"}
        subtitle={selected ? `${String(selected.subject || "").trim() || "Görüşme"} · ${fullDateTime(selected.date)}` : ""}
        readOnly={!canEdit}
        patientLink={selected?.patient?._id ? `/Hastalar/${selected.patient._id}?tab=telefon` : null}
      >
        {selected && (
          <PhoneCallEditor
            key={selected._id}
            record={selected}
            mode="standalone"
            patientId={selected.patient?._id}
            token={token}
            canEdit={canEdit}
            onSaved={handleSaved}
            onError={handleEditorError}
          />
        )}
      </RecordEditorDrawer>
    </>
  );
}
